import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react'
import {
  YESHUMAN_CONTACT_SHEET_CLOSE_MS,
  YeshumanContactSheet,
} from '@/components/landing/yeshuman/yeshuman-contact-sheet'

type YeshumanContactSheetContextValue = {
  open: boolean
  openContactSheet: () => void
  closeContactSheet: () => void
}

const YeshumanContactSheetContext = createContext<YeshumanContactSheetContextValue | null>(null)

export function useYeshumanContactSheet() {
  const ctx = useContext(YeshumanContactSheetContext)
  if (!ctx) {
    throw new Error('useYeshumanContactSheet must be used within YeshumanContactSheetProvider')
  }
  return ctx
}

function isTalkHref(href: string | null) {
  if (!href) return false
  return href === '#talk' || href === '/#talk' || href.endsWith('#talk')
}

/**
 * Contact drawer state for the Yes Human landing.
 * Uses a fixed portal host (same idea as the app page pane) so the pane Sheet
 * can slide over the page without covering it in a solid shell background.
 */
export function YeshumanContactSheetProvider({ children }: { children: ReactNode }) {
  const [portalHost, setPortalHost] = useState<HTMLElement | null>(null)
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [formKey, setFormKey] = useState(0)

  const openContactSheet = useCallback(() => {
    setFormKey((key) => key + 1)
    setMounted(true)
    setOpen(true)
  }, [])

  const closeContactSheet = useCallback(() => {
    setOpen(false)
  }, [])

  // Keep the Sheet mounted through the close animation (same as ArtifactCanvasProvider).
  useEffect(() => {
    if (open || !mounted) return
    const timeout = window.setTimeout(() => {
      setMounted(false)
    }, YESHUMAN_CONTACT_SHEET_CLOSE_MS)
    return () => window.clearTimeout(timeout)
  }, [open, mounted])

  // One-shot deep link: /#talk opens the drawer once on first paint.
  useEffect(() => {
    if (window.location.hash === '#talk' || window.location.hash === '#talk/') {
      setMounted(true)
      setOpen(true)
    }
  }, [])

  // Nav / footer links still use href="#talk" — intercept without changing the URL.
  useEffect(() => {
    const handleTalkClick = (event: globalThis.MouseEvent) => {
      if (event.defaultPrevented) return
      if (event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      if (!(event.target instanceof Element)) return
      const link = event.target.closest('a[href]')
      if (!(link instanceof HTMLAnchorElement)) return
      if (!isTalkHref(link.getAttribute('href'))) return
      event.preventDefault()
      event.stopPropagation()
      openContactSheet()
    }
    document.addEventListener('click', handleTalkClick, true)
    return () => document.removeEventListener('click', handleTalkClick, true)
  }, [openContactSheet])

  const value = useMemo(
    () => ({ open, openContactSheet, closeContactSheet }),
    [open, openContactSheet, closeContactSheet],
  )

  return (
    <YeshumanContactSheetContext.Provider value={value}>
      {children}
      {/*
        Viewport host for pane Sheet (absolute positioning). pointer-events-none so the
        page stays clickable; the pane itself sets pointer-events-auto.
      */}
      <div
        ref={setPortalHost}
        className="pointer-events-none fixed inset-0 z-50"
        aria-hidden
      />
      {mounted && portalHost ? (
        <YeshumanContactSheet
          open={open}
          formKey={formKey}
          portalContainer={portalHost}
          onOpenChange={(next) => {
            if (next) {
              setMounted(true)
              setOpen(true)
            } else {
              setOpen(false)
            }
          }}
        />
      ) : null}
    </YeshumanContactSheetContext.Provider>
  )
}

export function useOpenYeshumanContactSheet() {
  const { openContactSheet } = useYeshumanContactSheet()
  return useCallback(
    (event?: MouseEvent<HTMLElement>) => {
      event?.preventDefault()
      openContactSheet()
    },
    [openContactSheet],
  )
}
